import { Receipt } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  ProgressMeter,
  StatCard,
  StatCardRow,
} from '@/modules/common/components/molecules/stat-card';

import { getRatioColor } from '../../../helpers/progress-color.helper';
import { ApprovalsSummary } from '../../../types/time-tracking-approvals';
import { SummaryChip } from '../shared/summary-chip';
import { SummaryTooltipRows } from '../shared/summary-tooltip-rows';
import { useSummaryFormat } from '../shared/use-summary-format';

interface ApprovedForBillCardProps {
  summary: ApprovalsSummary;
}

/** Mirrors the invoice card, against the bill-side approved hours. */
const useApprovedForBill = (summary: ApprovalsSummary) => {
  const approved = summary.approvedForBillHours;
  const submitted = summary.awaitingApprovalHours;
  const approvableTotal = approved + submitted;

  return {
    approved,
    submitted,
    approvableTotal,
    progressPct: approvableTotal > 0 ? (approved / approvableTotal) * 100 : null,
    // Signed, so a bill total above the invoice total reads as a positive delta.
    vsInvoiceDelta: approved - summary.approvedForInvoiceHours,
  };
};

const useApprovedForBillTooltip = (summary: ApprovalsSummary) => {
  const { t } = useTranslation('authenticated.timeTracking');
  const { formatHoursAmount, formatSignedHours } = useSummaryFormat();
  const { approved, submitted, vsInvoiceDelta } = useApprovedForBill(summary);

  return (
    <SummaryTooltipRows
      title={t('approvals.summaryCards.approvedForBill.title')}
      rows={[
        {
          label: t('approvals.summaryCards.approvedForBill.approvedLabel'),
          value: formatHoursAmount(approved),
        },
        {
          label: t('approvals.summaryCards.shared.awaitingLabel'),
          value: formatHoursAmount(submitted),
        },
        {
          label: t('approvals.summaryCards.approvedForBill.deltaLabel'),
          value: formatSignedHours(vsInvoiceDelta),
        },
      ]}
      footer={t('approvals.summaryCards.approvedForBill.purposeNote')}
    />
  );
};

/**
 * Hours the member will be paid for.
 *
 * Shows hours ONLY, never money: a bill-derived monetary value would expose employee cost,
 * which this screen must not do.
 */
export const ApprovedForBillCard = ({ summary }: ApprovedForBillCardProps) => {
  const { t } = useTranslation('authenticated.timeTracking');
  const { formatHoursAmount, formatPct, formatSignedHours } = useSummaryFormat();
  const { approved, submitted, approvableTotal, progressPct, vsInvoiceDelta } =
    useApprovedForBill(summary);

  return (
    <StatCard
      icon={<Receipt size={16} aria-hidden />}
      title={t('approvals.summaryCards.approvedForBill.title')}
      infoTooltip={t('approvals.summaryCards.approvedForBill.purposeNote')}
    >
      <StatCardRow
        label={t('approvals.summaryCards.shared.progressLabel')}
        value={
          progressPct === null ? (
            <span className="text-muted-foreground">
              {t('approvals.summaryCards.shared.noValue')}
            </span>
          ) : (
            <span
              className="text-base font-semibold tabular-nums"
              style={{ color: getRatioColor(progressPct) }}
            >
              {formatPct(progressPct)}
            </span>
          )
        }
      />
      <StatCardRow
        label={t('approvals.summaryCards.approvedForBill.deltaLabel')}
        value={
          <span className="whitespace-nowrap tabular-nums">
            {formatSignedHours(vsInvoiceDelta)}
          </span>
        }
      />
      <ProgressMeter
        progress={approvableTotal > 0 ? approved / approvableTotal : null}
        filled={{
          label: t('approvals.summaryCards.approvedForBill.approvedShortLabel'),
          amount: approved,
        }}
        track={{ label: t('approvals.summaryCards.shared.awaitingLabel'), amount: submitted }}
        formatAmount={formatHoursAmount}
      />
    </StatCard>
  );
};

export const ApprovedForBillChip = ({ summary }: ApprovedForBillCardProps) => {
  const { t } = useTranslation('authenticated.timeTracking');
  const { formatPct } = useSummaryFormat();
  const { progressPct } = useApprovedForBill(summary);
  const tooltip = useApprovedForBillTooltip(summary);

  return (
    <SummaryChip
      icon={<Receipt className="h-3 w-3" />}
      label={t('approvals.summaryCards.approvedForBill.chipLabel')}
      value={
        progressPct === null ? t('approvals.summaryCards.shared.noValue') : formatPct(progressPct)
      }
      tooltip={tooltip}
    />
  );
};
